import { Toast } from "../Notifications/Toast";

export class PerfectShotReviewDialog extends HTMLElement {
    IMAGES_URL = "/perfect-shot/cases/";        // + {caseId}/images
    REVIEW_URL = "/perfect-shot/cases/review";

    ERROR_TEXT = {
        not_visible: "This case is no longer available to you — it may have moved to another clinic.",
        read_only: "Your account has read-only access to Perfect Shot reviews.",
        not_reviewable: "This case has no completed analysis to review yet.",
        stale: "The analysis for this case changed while this dialog was open. Refresh the page and review again.",
        incomplete: "Every flagged image needs a reason before the case can be reviewed.",
        invalid_reason: "One of the selected reasons is no longer valid. Refresh the page and try again.",
    };

    caseId = 0;
    payload = null;

    connectedCallback() {
        if (this.initialized) {
            return;                     // one instance per host, built once
        }
        this.initialized = true;
        this.createDialog();
        this.initEventListeners();
    }

    /**
     * The whole input contract (§7.4): the host hands us a case id and nothing else.
     * A method, not an attribute — re-opening the same case twice must work.
     */
    open(caseId, { docked = false } = {}) {
        this.caseId = parseInt(caseId);
        this.payload = null;
        this.dialog.classList.toggle('ps-review-dlg--docked', docked);
        this.renderLoading();
        if (!this.dialog.open) {
            docked ? this.dialog.show() : this.dialog.showModal();
        }
        this.fetchFindings();
    }

    /** single source of truth for "is the dialog up" — PerfectShotCases skips its poll on this */
    get isOpen() { return this.dialog?.open === true; }

    get body() { return this.dialog.querySelector('.dlg-body'); }
    get status() { return this.dialog.querySelector('.dlg-status'); }
    get confirm() { return this.dialog.querySelector('.confirm-btn'); }

    /** every image block that needs a reason before we can submit */
    get flaggedBlocks() { return [...this.dialog.querySelectorAll('.ps-image[data-flagged="1"]')]; }

    createDialog() {
        this.dialog = document.createElement('dialog');
        this.dialog.classList.add('modal-dlg', 'ps-review-dlg');
        this.dialog.id = 'perfect-shot-review-dialog';
        this.dialog.innerHTML = `
            <div class="dlg-content">
                <div class="dlg-header">
                    <h2></h2>
                    <button type="button" class="close-btn" aria-label="Close">&times;</button>
                </div>
                <div class="dlg-body"></div>
                <div class="dlg-footer">
                    <p class="dlg-status" role="status"></p>
                    <div class="btn-div">
                        <button type="button" class="cancel-btn">Cancel</button>
                        <button type="button" class="confirm-btn" disabled>Mark Reviewed</button>
                    </div>
                </div>
            </div>`;
        this.appendChild(this.dialog);
    }

    initEventListeners() {
        this.dialog.addEventListener('click', event => this.handleClick(event));
        this.dialog.addEventListener('change', event => this.handleChange(event));

        // Esc / backdrop while a POST is in flight would leave the user unsure whether it landed
        this.dialog.addEventListener('cancel', event => {
            if (this.submitting) {
                event.preventDefault();
            }
        });

        this.dialog.addEventListener('close', () => {
            this.payload = null;
            this.body.innerHTML = '';
        });

        // thumbnails 404 wherever the image host isn't reachable — drop them, keep the row
        this.dialog.addEventListener('error', event => {
            if (event.target.matches('.ps-image__thumb')) {
                event.target.remove();
            }
        }, true);

        this.dialog.addEventListener('close', () => {
            this.payload = null;
            this.body.innerHTML = '';
            this.dispatchEvent(new CustomEvent('perfect-shot-review-closed', { bubbles: true }));
        });
    }

    handleClick(event) {
        if (event.target.closest('.close-btn, .cancel-btn')) {
            this.dialog.close();
            return;
        }
        if (event.target.closest('.confirm-btn')) {
            this.submit();
            return;
        }

        const thumb = event.target.closest('.ps-image__thumb');
        if (thumb) {
            this.dispatchEvent(new CustomEvent('perfect-shot-image-selected', {
                bubbles: true,
                detail: { dicom_id: parseInt(thumb.closest('.ps-image').dataset.dicom_id) },
            }));
            return;
        }

        const chevron = event.target.closest('.ps-image__chevron');
        if (chevron) {
            const block = chevron.closest('.ps-image');
            block.classList.toggle('expanded');
            chevron.setAttribute('aria-expanded', block.classList.contains('expanded'));
            return;
        }

        const clear = event.target.closest('.ps-reason__clear');
        if (clear) {
            const fieldset = clear.closest('.ps-reason');
            fieldset.querySelectorAll('input[type="radio"]').forEach(radio => {
                radio.checked = false;
            });
            fieldset.querySelector('textarea').value = '';
            this.refreshConfirmState();
            return;
        }

        const applyToggle = event.target.closest('.ps-apply__toggle');
        if (applyToggle) {
            const opening = applyToggle.getAttribute('aria-expanded') !== 'true';
            if (opening) {
                this.refreshApplyOptions(applyToggle.closest('.ps-apply'));
            }
            applyToggle.setAttribute('aria-expanded', opening);
            return;
        }

        if (event.target.closest('.ps-apply__go')) {
            this.applyToOthers(event.target.closest('.ps-image'));
        }
    }

    handleChange(event) {
        if (event.target.matches('.ps-reason input[type="radio"]')) {
            this.refreshConfirmState();
        }
    }

    /**
     * An image that already carries a reason is off limits — applying would silently
     * overwrite an answer someone gave deliberately.  Recomputed each time the fold opens,
     * because the other images' answers change while this one sits closed.
     */
    refreshApplyOptions(fold) {
        let available = 0;

        fold.querySelectorAll('.ps-apply__option').forEach(option => {
            const box = option.querySelector('input[type="checkbox"]');
            const target = this.dialog.querySelector(`.ps-image[data-dicom_id="${box.value}"]`);
            const flagged = target?.dataset.flagged === '1';
            const answered = !!target?.querySelector('.ps-reason input:checked');
            const selectable = flagged && !answered;

            // Every image is listed so the numbers line up with the blocks above, but only a
            // flagged image with no answer yet can actually take one.
            box.disabled = !selectable;
            option.classList.toggle('is-answered', answered);
            option.classList.toggle('is-unflagged', !flagged);
            option.title = answered ? 'Already has a reason'
                : !flagged ? 'No issues — nothing to justify'
                    : '';

            if (selectable) {
                available++;
            } else {
                box.checked = false;
            }
        });

        fold.classList.toggle('is-exhausted', available === 0);
        fold.querySelector('.ps-apply__go').disabled = available === 0;
    }

    /** copy this block's reason + notes into every ticked image, then collapse the fold */
    applyToOthers(sourceBlock) {
        const reason = sourceBlock.querySelector('.ps-reason input[type="radio"]:checked')?.value;
        if (!reason) {
            this.showStatus('Choose a reason above before applying it to other images.');
            return;
        }
        const notes = sourceBlock.querySelector('.ps-notes textarea').value;

        sourceBlock.querySelectorAll('.ps-apply__panel input[type="checkbox"]:checked').forEach(box => {
            const target = this.dialog.querySelector(`.ps-image[data-dicom_id="${box.value}"]`);
            if (!target) {
                return;
            }
            if (target.querySelector('.ps-reason input:checked')) {
                return;              // answered since the fold opened — never overwrite it
            }
            const radio = target.querySelector(`.ps-reason input[value="${reason}"]`);
            if (radio) {
                radio.checked = true;
            }
            const targetNotes = target.querySelector('.ps-notes textarea');
            if (notes && !targetNotes.value.trim()) {
                targetNotes.value = notes;      // never clobber notes typed for that image
            }
            box.checked = false;
        });

        sourceBlock.querySelector('.ps-apply__toggle').setAttribute('aria-expanded', 'false');
        this.showStatus('');
        this.refreshConfirmState();
    }

    /** 26981: the confirm button stays disabled until every flagged image carries a reason */
    refreshConfirmState() {
        const ready = this.payload?.can_review === true
            && this.flaggedBlocks.every(block => block.querySelector('.ps-reason input:checked'));
        this.confirm.disabled = !ready || this.submitting === true;

        // A fold's usefulness depends on the OTHER images, so any answer can change every
        // fold — and every answer change funnels through here.
        this.dialog.querySelectorAll('.ps-apply').forEach(fold => this.refreshApplyOptions(fold));
    }

    fetchFindings() {
        const caseId = this.caseId;                 // guard against a second open() overtaking this one

        fetch(`${this.IMAGES_URL}${caseId}/images`, { headers: { Accept: 'application/json' } })
            .then(response => response.json().then(data => ({ ok: response.ok, data })))
            .then(({ ok, data }) => {
                if (caseId !== this.caseId || !this.isOpen) {
                    return;
                }
                if (!ok || !data.success) {
                    this.renderError(this.ERROR_TEXT[data.error] ?? 'Could not load this case for review.');
                    return;
                }
                this.payload = data;
                this.renderPayload();
            })
            .catch(error => {
                console.error('Error fetching Perfect Shot review info:', error);
                if (caseId === this.caseId) {
                    this.renderError('Could not reach the server. Check your connection and try again.');
                }
            });
    }

    submit() {
        if (this.submitting) {
            return;
        }
        this.submitting = true;
        this.confirm.disabled = true;
        this.showStatus('Saving…');

        const images = this.flaggedBlocks.map(block => ({
            dicom_id: parseInt(block.dataset.dicom_id),
            reason: block.querySelector('.ps-reason input:checked').value,
            notes: block.querySelector('.ps-notes textarea').value.trim() || null,
        }));

        fetch(this.REVIEW_URL, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ case_id: this.caseId, images }),
        })
            .then(response => response.json().then(data => ({ ok: response.ok, data })))
            .then(({ ok, data }) => {
                this.submitting = false;
                if (!ok || !data.success) {
                    this.showStatus(this.ERROR_TEXT[data.error] ?? 'The review could not be saved.');
                    this.refreshConfirmState();
                    return;
                }
                // Hosts only ever hear about success (§7.4) — the fingerprint is opaque, pass it through.
                document.body.dispatchEvent(new CustomEvent('perfect-shot-case-reviewed', {
                    detail: {
                        case_id: this.caseId,
                        fingerprint: data.fingerprint,
                        review: data.review ?? null,
                        images: images.map(i => ({
                            dicom_id: i.dicom_id,
                            reason: i.reason,
                            reason_label: this.payload.reasons.find(r => r.key === i.reason)?.label ?? i.reason,
                            notes: i.notes,
                        })),
                    },
                }));
                const toast = new Toast({
                    headerContent: 'Reviewed Successfully',
                    bodyContent: 'You have successfully submitted your review',
                    toastClassList: 'ps-review-success'
                });

                this.dialog.close();
                toast.show();
            })
            .catch(error => {
                console.error('Error saving Perfect Shot review:', error);
                this.submitting = false;
                this.showStatus('Could not reach the server. Your review was not saved.');
                this.refreshConfirmState();
            });
    }

    showStatus(message) {
        this.status.textContent = message;
    }

    renderLoading() {
        this.dialog.querySelector('.dlg-header h2').textContent = 'Perfect Shot Review';
        this.body.innerHTML = '<p class="ps-loading">Loading image findings…</p>';
        this.confirm.disabled = true;
        this.confirm.hidden = false;
        this.showStatus('');
    }

    renderError(message) {
        this.body.innerHTML = `<p class="ps-error">${this.esc(message)}</p>`;
        this.confirm.hidden = true;
        this.showStatus('');
    }

    renderPayload() {
        const data = this.payload;

        this.dialog.querySelector('.dlg-header h2').textContent =
            `${data.patient_name} — Case ${data.case_id}`;

        // Position in the case is the only thing that tells two "VD Abdomen" films apart,
        // so it is assigned once here and used by both the blocks and the apply lists.
        const numbered = data.images.map((image, index) => ({ ...image, number: index + 1 }));

        this.body.innerHTML = `
            <div class="ps-review__meta">
                <span class="quality-badge quality-badge--${this.esc(data.quality)}">${this.esc(data.quality)}</span>
                <span>${this.esc(data.study_type)}</span>
                <span>${this.esc(data.study_date ?? '')}</span>
            </div>
            ${data.can_review ? '' : '<p class="ps-readonly-note">Read-only — your account cannot record reviews.</p>'}
            ${data.images.length === 0
                ? '<p class="ps-empty">No analyzed images for this case yet.</p>'
                : `<div class="ps-images">${numbered.map(image =>
                    this.imageBlock(image, numbered)).join('')}</div>`}`;

        this.confirm.hidden = !data.can_review;
        this.refreshConfirmState();
    }

    /**
     * Flagged images render expanded with a required reason group; pass images collapse to
     * their summary row (26980 still wants every image listed) and open on the chevron.
     */
    imageBlock(image, numbered) {
        const isFlagged = image.flagged === true;
        const others = numbered.filter(other => other.dicom_id !== image.dicom_id);
        const canRespond = isFlagged && this.payload.can_review;

        return `
            <section class="ps-image ps-image--${this.esc(image.quality)}${isFlagged ? ' expanded' : ''}"
                     data-dicom_id="${image.dicom_id}"
                     data-flagged="${canRespond ? 1 : 0}">
                <header class="ps-image__summary">
                    <span class="ps-num">${image.number}</span>
                    <img class="ps-image__thumb" src="${this.esc(image.thumbnail_url)}" alt="" loading="lazy">
                    <span class="ps-image__label">${this.esc(image.label)}</span>
                    <span class="quality-badge quality-badge--${this.esc(image.quality)}">${this.esc(image.quality)}</span>
                    <button type="button" class="ps-image__chevron" aria-expanded="${isFlagged}"
                            aria-label="Toggle details for ${this.esc(image.label)}"></button>
                </header>
                <div class="ps-image__detail">
                    <div class="ps-image__detail-inner">
                        <div class="ps-image__detail-body">
                            ${image.issues.length === 0
                ? '<p class="ps-issues--none">No issues detected on this image.</p>'
                : `<ul class="ps-issues">${image.issues.map(issue => this.issueItem(issue)).join('')}</ul>`}
                            ${canRespond ? this.reasonFieldset(image, others) : ''}
                        </div>
                    </div>
                </div>
            </section>`;
    }

    issueItem(issue) {
        return `
            <li class="ps-issue ps-issue--${this.esc(issue.severity)}">
                <p class="ps-issue__text">${this.esc(issue.text)}</p>
                ${issue.why ? `<p class="ps-issue__why"><strong>Why this matters:</strong> ${this.esc(issue.why)}</p>` : ''}
                ${issue.fix ? `<p class="ps-issue__fix"><strong>How to fix:</strong> ${this.esc(issue.fix)}</p>` : ''}
            </li>`;
    }

    reasonFieldset(image, others) {
        return `
            <fieldset class="ps-reason">
                <legend>Reason for proceeding <span class="required">*</span></legend>
                <button type="button" class="ps-reason__clear">Clear</button>
                ${this.payload.reasons.map(reason => `
                    <label class="ps-reason__option">
                        <input type="radio" name="ps_reason_${image.dicom_id}" value="${this.esc(reason.key)}">
                        <span>${this.esc(reason.label)}</span>
                    </label>`).join('')}
                ${others.length > 0 ? this.applyFold(others) : ''}
                <label class="ps-notes">
                    <span>Notes (optional)</span>
                    <textarea rows="2" maxlength="1000"></textarea>
                </label>
            </fieldset>`;
    }

    /** 26981's study-level override dropdown, inlined under the first flagged image */
    applyFold(others) {
        return `
            <div class="ps-apply">
                <button type="button" class="ps-apply__toggle" aria-expanded="false">
                    Apply this response to other images
                </button>
                <div class="ps-apply__panel">
                    <div class="ps-apply__panel-inner">
                        <div class="ps-apply__panel-box">
                            ${others.map(other => `
                                <label class="ps-apply__option">
                                    <input type="checkbox" value="${other.dicom_id}">
                                    <span class="ps-num">${other.number}</span>
                                    <span>${this.esc(other.label)}</span>
                                </label>`).join('')}
                            <button type="button" class="ps-apply__go">Apply</button>
                        </div>
                    </div>
                </div>
            </div>`;
    }

    esc(value) {
        return String(value ?? '').replace(/[&<>"']/g, character => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[character]));
    }
}

customElements.define('perfect-shot-review-dialog', PerfectShotReviewDialog);